import { SETTINGS } from "@Config";
import { Logger, LoggerService } from "@Shared/logger/index";
import { DataUtil, DomainUtil } from "@Shared/utils";
import { PluginClass } from "./plugin";
import { MNGAuthentication } from "@Core";
import './styles/index.scss';
import { AuthenticationUtils, AuthModule } from "@Modules/auth_module";
import { EntitlementService } from "@Modules/entitlement_module";
import { OneTapModule } from "@Modules/onetap_module";
import { PageSuiteService } from "@Modules/pagesuite_module";
import { ScreenType, UIModule } from "@Modules/ui_module";
import { Blueconic } from "@Modules/blueconic_module";

const Log: Logger = LoggerService.createLogger("Entry");
Log.trace("MG2 Auth Plugin Loaded");
let pluginInitialed = false;
// Resolves once PluginClass.init() has fully finished
// Starts pre-resolved so calls made before init() is ever invoked don't hang.
let pluginReadyPromise: Promise<void> = Promise.resolve();
start();


declare global {
  interface Window {
    MG2Loader: { [method: string]: (...args: any[]) => any; };
    MNGAuthentication?: MNGAuthentication;
    dataLayer?: Record<string, any>[]
  }
}

function onDOMReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    // Still loading — wait for the event
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    // `interactive` or `complete` — DOM is already ready
    fn();
  }
}

export function init(options?: any): void {


  if (pluginInitialed) {
    Log.trace("Plugin Already Initialized");
    return;
  }

  const plugin = new PluginClass();
  pluginReadyPromise = plugin.init(options);
  Log.trace("Plugin Initialized");
  pluginInitialed = true;
}


export function setLoggerEnabled(val: boolean): void {
  Log.info("setLoggerEnabled");
  LoggerService.setEnabled(val);


}

export function setLogLevel(val: string): void {
  Log.info("setLogLevel");
  LoggerService.setLevel(val);


}


// Expose the customLogout flow as the main logout
export async function logout(): Promise<void> {
  await pluginReadyPromise;
  await AuthModule.logout();
}

// Expose logout for debugging via the browser console.
if (typeof window !== "undefined") {
  AuthenticationUtils.initializeMNGAuthenticationGlobal()
  window.MNGAuthentication!.customLogout = logout;
  bindLogoutClickHandler();
}

// Delegated click handler: any element with the `digisubs-logout` class
// (or nested inside one) triggers the customLogout flow when clicked.
function bindLogoutClickHandler(): void {
  document.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as Element | null;
    const trigger = target?.closest?.('.digisubs-logout');
    if (!trigger) {
      return;
    }
    event.preventDefault();
    Log.trace("digisubs-logout clicked, calling customLogout");
    // customLogout is async; surface failures instead of letting the rejection vanish.
    Promise.resolve(window.MNGAuthentication?.customLogout?.()).catch((err) => {
      Log.error("customLogout failed", err);
    });
  });
}

export function isFrontPage(): boolean {
  const isFront = PageSuiteService.Instance.IsFront;
  Log.trace("isFrontPage", isFront);
  return isFront;

}

export async function checkEntitlements(): Promise<void> {
  Log.trace("Checking Entitlements....");
  const result = await EntitlementService.Instance.checkEntitlements();
  Log.trace("Entitlements Check Result", result);
}

export function getEntitlements(): any {
  const mngSession = EntitlementService.Instance.getMNGSession();
  Log.trace("getEntitlements", mngSession);
  
  return mngSession;
}

export function showScreen(name): void {
  UIModule.ScreenManager.showScreen(name);
}

export function promptOneTap(): Promise<void> {
  return OneTapModule.prompt();
}

export { Blueconic };


//internal method that is used to check if we have an autoInit param so we can manually call init method.
function start() {
  //check if we have autoInit params...
  const autoInit = DomainUtil.getUrlParamFromScriptTag('mng-auth', 'autoInit');
  if (autoInit) {
    onDOMReady(() => {
      Log.trace("AutoInit is set to true, so we will init the plugin.");
      //we should auto init so we also need to load CSS since we would not just have the site add the css file unless we can't manually call init.
      // Load the plugin CSS, but don't let a failed CSS stop the plugin from initializing
      DataUtil.loadCSS(SETTINGS.awsBaseUrl + "mng-auth.css")
          .then(() => {
            Log.trace("CSS Loaded");
          })
          .catch((err) => {
            Log.error("Error loading CSS", err);
          })
          .finally(() => {
            if (document.body) {
              void document.body.offsetHeight;
            }
            init();
          });
    });
    


  }
}


